import { ChevronDown, Square, SquareCheck } from 'lucide-react';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';

export type InboxSelectScope =
    | 'all'
    | 'none'
    | 'read'
    | 'unread'
    | 'needs_review';

type InboxSelectableRow = {
    readonly id: string;
    readonly unread_count: number;
    readonly pending_staged_message_count: number;
};

export function idsForInboxSelectScope(
    rows: ReadonlyArray<InboxSelectableRow>,
    scope: InboxSelectScope,
): ReadonlyArray<string> {
    switch (scope) {
        case 'all':
            return rows.map((row) => row.id);
        case 'none':
            return [];
        case 'read':
            // The complement of 'unread': a needs-review row whose messages
            // have been read belongs here too.
            return rows
                .filter((row) => row.unread_count === 0)
                .map((row) => row.id);
        case 'unread':
            return rows
                .filter((row) => row.unread_count > 0)
                .map((row) => row.id);
        case 'needs_review':
            return rows
                .filter((row) => row.pending_staged_message_count > 0)
                .map((row) => row.id);
        default: {
            const unreachable: never = scope;
            throw new Error(`Unhandled select scope: ${unreachable}`);
        }
    }
}

export function nextMasterSelection(
    current: ReadonlyArray<string>,
    visibleIds: ReadonlyArray<string>,
): ReadonlyArray<string> {
    return current.length === 0 ? visibleIds : [];
}

const SCOPES: ReadonlyArray<{
    readonly scope: InboxSelectScope;
    readonly label: string;
}> = [
    { scope: 'all', label: 'All' },
    { scope: 'none', label: 'None' },
    { scope: 'read', label: 'Read' },
    { scope: 'unread', label: 'Unread' },
    { scope: 'needs_review', label: 'Needs Review' },
];

type ConversationInboxSelectSplitProps = {
    readonly hasSelection: boolean;
    readonly onToggleMaster: () => void;
    readonly onSelectScope: (scope: InboxSelectScope) => void;
};

export function ConversationInboxSelectSplit({
    hasSelection,
    onToggleMaster,
    onSelectScope,
}: ConversationInboxSelectSplitProps) {
    // Quo’s header uses compact rounded-full pills (Open + chevron), not
    // Gmail’s boxed checkbox|divider|chevron. Same actions, Quo chrome.
    return (
        <div
            className={cn(
                // shrink-0: the pill row is nowrap at md+, and without this
                // the checkbox is the first thing squeezed into a sliver.
                'flex h-6 shrink-0 items-stretch overflow-hidden rounded-full',
                hasSelection
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300'
                    : 'bg-muted text-foreground',
            )}
            data-testid="conversations-inbox-select-split"
        >
            <button
                aria-label={
                    hasSelection
                        ? 'Clear selection'
                        : 'Select all conversations'
                }
                aria-pressed={hasSelection}
                // The pill is an oval, so its halves have to be too. They
                // show focus as a fill rather than a ring: the pill clips
                // its children, so a ring would survive only along the seam
                // between the halves and read as a stray divider.
                className="flex items-center rounded-l-full px-2 outline-none hover:bg-black/5 focus-visible:bg-black/10 dark:hover:bg-white/10 dark:focus-visible:bg-white/20"
                onClick={onToggleMaster}
                type="button"
            >
                {hasSelection ? (
                    <SquareCheck className="size-3.5" />
                ) : (
                    <Square className="size-3.5" />
                )}
            </button>
            <DropdownMenu>
                <DropdownMenuTrigger asChild={true}>
                    <button
                        aria-label="Selection options"
                        className="flex items-center rounded-r-full px-1.5 outline-none hover:bg-black/5 focus-visible:bg-black/10 dark:hover:bg-white/10 dark:focus-visible:bg-white/20"
                        type="button"
                    >
                        <ChevronDown className="size-3" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    {SCOPES.map((item) => (
                        <DropdownMenuItem
                            key={item.scope}
                            onClick={() => onSelectScope(item.scope)}
                        >
                            {item.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
